import React, { useCallback, useMemo } from 'react';

import { find, get, head, isEmpty, map } from 'lodash';

import { EmptyStateList } from 'components/v2';
import useAuditsMany from '../hooks/useAuditsMany';

import ListItemAudit from './ListItemAudit';
import ListNodes, { ListNodesProps } from './ListNodes';
import { Audit } from './types/auditType';
import ListAuditsMessageTemplateSkeleton from './ListAuditsMessageTemplateSkeleton';

export interface ListAuditsMessageTemplateProps extends Omit<ListNodesProps, 'classes' | 'onNodeSelected'> {
  modelId: string;
  onNodeSelected: (event: React.UIEvent | React.SyntheticEvent, node: Audit) => void;
}

/**
 * List of audits for the `MessageTemplate.template` field
 */
const ListAuditsMessageTemplate: React.FC<ListAuditsMessageTemplateProps> = ({
  modelId,
  children,
  idsSelected: idsSelectedProp,
  onNodeSelected,
  ...rest
}) => {
  const { audits, queryStatus } = useAuditsMany({ modelId });

  const idsSelected = useMemo(() => {
    if (isEmpty(idsSelectedProp)) {
      // Select first audit (current audit) by default
      return [get(head(audits), '_id') as string];
    }
    return idsSelectedProp;
  }, [idsSelectedProp, audits]);

  const handleNodeSelected = useCallback(
    (event, node) => {
      const audit = find(audits, { _id: node._id });
      return onNodeSelected(event, { ...node, description: get(audit, 'description', '') });
    },
    [audits, onNodeSelected],
  );

  return (
    <ListNodes idsSelected={idsSelected} onNodeSelected={handleNodeSelected} {...rest}>
      {queryStatus === 'loading' && <ListAuditsMessageTemplateSkeleton numberOfRows={3} height={90} width="100%" />}
      {queryStatus !== 'loading' && isEmpty(audits) ? (
        <EmptyStateList />
      ) : (
        map(audits, (audit, index) => (
          <ListItemAudit key={audit._id} audit={audit} description={audit.description} current={index === 0} />
        ))
      )}
      {queryStatus !== 'loading' && children}
    </ListNodes>
  );
};

export default ListAuditsMessageTemplate;
